import {
  Debt,
  DebtOriginType,
  DebtPayment,
  DebtStatus,
  formatCurrency,
} from '@bofrak-backend/shared';
import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import Fuse from 'fuse.js';
import { formatDate } from '@bofrak-backend/shared-i18n';
import { useMemo, useState } from 'react';
import { FiArrowDownCircle, FiArrowUpCircle, FiSearch } from 'react-icons/fi';
import { tShared, useSharedUiTranslation } from '../../../i18n';
import { getDebtStatusLabel } from '../../utils/status-labels';

interface DebtHistoryTimelineProps {
  debts: Debt[];
  payments: DebtPayment[];
  isLoading: boolean;
}

type TimelineItem =
  | { type: 'debt'; date: string; data: Debt }
  | { type: 'payment'; date: string; data: DebtPayment };

const getOriginLabel = (origin: DebtOriginType): string => {
  const labels: Record<DebtOriginType, string> = {
    [DebtOriginType.RECEIPT]: tShared('Receipt'),
    [DebtOriginType.LOAN]: tShared('Loan'),
    [DebtOriginType.TRANSFER]: tShared('Transfer'),
  };
  return labels[origin] ?? origin;
};

const STATUS_COLORS: Record<DebtStatus, string> = {
  [DebtStatus.ACTIVE]: 'blue',
  [DebtStatus.OVERDUE]: 'red',
  [DebtStatus.DEFAULTED]: 'red',
  [DebtStatus.CLOSED]: 'green',
  [DebtStatus.CANCELLED]: 'gray',
  [DebtStatus.TRANSFERRED]: 'gray',
  [DebtStatus.REVERSED]: 'orange',
};

export function DebtHistoryTimeline({
  debts,
  payments,
  isLoading,
}: DebtHistoryTimelineProps) {
  const { t } = useSharedUiTranslation();
  const [query, setQuery] = useState('');

  // Merge debts and payments into a single timeline sorted by date desc
  const allItems = useMemo<TimelineItem[]>(() => {
    const debtItems: TimelineItem[] = debts.map((d) => ({
      type: 'debt',
      date: d.created_at,
      data: d,
    }));
    const paymentItems: TimelineItem[] = payments.map((p) => ({
      type: 'payment',
      date: p.created_at,
      data: p,
    }));
    return [...debtItems, ...paymentItems].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [debts, payments]);

  // Search across all items
  const fuse = useMemo(
    () =>
      new Fuse(allItems, {
        keys: [
          'data.debtor_name',
          'data.payer_name',
          'data.reason',
          'data.creator_name',
          'data.id',
          'data.debt_id',
          'data.note',
        ],
        threshold: 0.3,
      }),
    [allItems],
  );

  const filteredItems = useMemo(() => {
    if (!query) return allItems;
    return fuse.search(query).map((r) => r.item);
  }, [allItems, query, fuse]);

  if (isLoading) {
    return (
      <VStack spacing={0} p={3}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} h="80px" w="full" mb={2} borderRadius="md" />
        ))}
      </VStack>
    );
  }

  return (
    <>
      <Box px={3} pt={3} pb={1}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <FiSearch color="gray" />
          </InputLeftElement>
          <Input
            placeholder={t('Search debts or payments...')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            borderRadius="md"
            fontSize="sm"
          />
        </InputGroup>
      </Box>

      {filteredItems.length === 0 ? (
        <Box py={12} textAlign="center">
          <Text color="gray.500" fontSize="sm">
            {query
              ? t('No matching records')
              : t('No debt activity in this period')}
          </Text>
        </Box>
      ) : (
        <VStack spacing={0} align="stretch" pb={4}>
          {filteredItems.map((item, idx) => (
            <TimelineEntry
              key={`${item.type}-${item.type === 'debt' ? item.data.id : item.data.id}-${idx}`}
              item={item}
              isLast={idx === filteredItems.length - 1}
            />
          ))}
        </VStack>
      )}
    </>
  );
}

function TimelineEntry({
  item,
  isLast,
}: {
  item: TimelineItem;
  isLast: boolean;
}) {
  const isDebt = item.type === 'debt';
  const accentColor = isDebt ? 'blue.500' : 'green.500';

  return (
    <HStack spacing={0} align="stretch" px={3}>
      {/* Timeline line + dot */}
      <VStack spacing={0} w="24px" align="center" flexShrink={0}>
        <Box
          w="10px"
          h="10px"
          borderRadius="full"
          bg={accentColor}
          mt="14px"
          flexShrink={0}
        />
        {!isLast && <Box w="2px" flex={1} bg="gray.200" />}
      </VStack>

      {/* Content */}
      <Box flex={1} pb={3} pl={2}>
        {isDebt ? (
          <DebtTimelineCard debt={item.data as Debt} />
        ) : (
          <PaymentTimelineCard payment={item.data as DebtPayment} />
        )}
      </Box>
    </HStack>
  );
}

function DebtTimelineCard({ debt }: { debt: Debt }) {
  const { t } = useSharedUiTranslation();
  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="blue.100"
      borderRadius="md"
      p={3}>
      <Flex justify="space-between" align="center" mb={1}>
        <HStack spacing={1}>
          <Icon as={FiArrowUpCircle} color="blue.500" boxSize={3.5} />
          <Text fontSize="xs" fontWeight="bold" color="blue.600">
            {t('Debt Given')}
          </Text>
        </HStack>
        <Badge
          colorScheme={STATUS_COLORS[debt.status]}
          variant="subtle"
          fontSize="9px">
          {getDebtStatusLabel(debt.status)}
        </Badge>
      </Flex>

      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" fontWeight="bold">
          {formatCurrency(debt.principal_amount, true)}
        </Text>
        <Badge colorScheme="purple" variant="outline" fontSize="9px">
          {getOriginLabel(debt.origin_type)}
        </Badge>
      </HStack>

      <Text fontSize="xs" fontWeight="500" color="gray.700" mb={0.5}>
        {debt.debtor_name}
      </Text>

      {debt.reason && (
        <Text fontSize="xs" color="gray.500" noOfLines={1} mb={0.5}>
          {debt.reason}
        </Text>
      )}

      <HStack justify="space-between" fontSize="9px" color="gray.400">
        <Text>
          {formatDate(new Date(debt.created_at), 'dd MMM yyyy, HH:mm')}
        </Text>
        <Text>{t('by {{name}}', { name: debt.creator_name })}</Text>
      </HStack>

      {debt.outstanding_balance > 0 && (
        <Text fontSize="xs" color="red.500" mt={0.5}>
          {t('Outstanding: {{amount}}', {
            amount: formatCurrency(debt.outstanding_balance, true),
          })}
        </Text>
      )}
    </Box>
  );
}

function PaymentTimelineCard({ payment }: { payment: DebtPayment }) {
  const { t } = useSharedUiTranslation();
  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="green.100"
      borderRadius="md"
      p={3}>
      <Flex justify="space-between" align="center" mb={1}>
        <HStack spacing={1}>
          <Icon as={FiArrowDownCircle} color="green.500" boxSize={3.5} />
          <Text fontSize="xs" fontWeight="bold" color="green.600">
            {t('Payment Received')}
          </Text>
        </HStack>
        {payment.payment_type_name && (
          <Badge colorScheme="gray" variant="subtle" fontSize="9px">
            {payment.payment_type_name}
          </Badge>
        )}
      </Flex>

      <Text fontSize="sm" fontWeight="bold" color="green.600" mb={0.5}>
        {formatCurrency(payment.amount, true)}
      </Text>

      <Text fontSize="xs" fontWeight="500" color="gray.700" mb={0.5}>
        {payment.payer_name}
      </Text>

      <Text fontSize="xs" fontWeight="500" color="gray.700" mb={0.5}>
        {payment.debt_id
          ? t('For Debt ID: {{id}}', { id: payment.debt_id })
          : t('No associated debt')}
      </Text>

      {payment.note && (
        <Text fontSize="xs" color="gray.500" noOfLines={1} mb={0.5}>
          {payment.note}
        </Text>
      )}

      <HStack justify="space-between" fontSize="9px" color="gray.400">
        <Text>
          {formatDate(new Date(payment.created_at), 'dd MMM yyyy, HH:mm')}
        </Text>
        <Text>
          {t('Balance: {{amount}}', {
            amount: formatCurrency(payment.outstanding_balance, true),
          })}
        </Text>
      </HStack>
    </Box>
  );
}
